import React, { useEffect, useState } from "react";
import FileInput from "../fileInput/FileInput";
import { getCardType } from "utils/parse/parse";
import { useTranslator } from "context/translator";
import FormError from "components/formError/FormError";
import { checkSensitiveInformationService } from "services/sensitiveCustomerInformation/services";
import { useSelector } from "react-redux";
import ClientRelatedTo from "components/clientRelatedTo/ClientRelatedTo";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const CardOwnerForm = ({ data, onChange, form, customerId }) => {
  const { translate } = useTranslator();
  const { user } = useSelector((state) => state.user);
  const [companyAllow, setCompanyAllow] = useState([]);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  const update = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleRelatedTo = (value) => {
    onChange({ ...data, relatedTo: value });
  };

  const handleDelete = () => {
    onChange({ ...data, status: !data.status });
  };

  const buildMaskedCard = (first6, last4) => {
    if (first6.length === 6 && last4.length === 4) {
      return `${first6}******${last4}`;
    }
    return "";
  };

  const handleFirst6Change = async (value) => {
    const clean = value.replace(/\D/g, "").slice(0, 6);
    const cardType = clean.length === 6 ? getCardType(clean) : "";

    let binCard = null;

    if (clean.length === 6) {
      binCard = await checkSensitiveInformationService({ bin: clean });
    }

    onChange({
      ...data,
      first6: clean,
      cardType,
      maskedCard: buildMaskedCard(clean, data.last4 || ""),
      cardNumber: buildMaskedCard(clean, data.last4 || ""),
      binInfo: !binCard?.error ? binCard : null,
    });
  };

  const handleLast4Change = (value) => {
    const clean = value.replace(/\D/g, "").slice(0, 4);

    onChange({
      ...data,
      last4: clean,
      maskedCard: buildMaskedCard(data.first6 || "", clean),
      cardNumber: buildMaskedCard(data.first6 || "", clean),
    });
  };

  useEffect(() => {
    if (company && company?.allow) {
      setCompanyAllow({ ...company?.allow });
    }
  }, [company]);

  if (userLoading || companyLoading) return null;

  return (
    <div className="border rounded-xl p-4 space-y-4 bg-white dark:bg-gray-900 shadow">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg dark:text-white">
          {translate("card")}
        </h3>

        {data?._id && (
          <>
            {data.status ? (
              <button
                type="button"
                onClick={handleDelete}
                className="text-red-600 text-sm"
              >
                {translate("deactivate-card")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDelete}
                className="text-green-600 text-sm"
              >
                {translate("activate-card")}
              </button>
            )}
          </>
        )}
      </div>

      {/* Owner info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">{translate("first-name")}</label>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            className="input"
            required
          />
          <FormError form={form} name="firstName" />
        </div>

        <div>
          <label className="label">{translate("last-name")}</label>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => update("lastName", e.target.value)}
            className="input"
            required
          />
          <FormError form={form} name="lastName" />
        </div>

        {/* First 6 */}
        <div>
          <label className="label">{translate("first-6-digits")}</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={data.first6 || ""}
            onChange={(e) => handleFirst6Change(e.target.value)}
            className="input"
            placeholder="123456"
          />
          <FormError form={form} name="first6" />
        </div>

        {/* Last 4 */}
        <div>
          <label className="label">{translate("last-4-digits")}</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={data.last4 || ""}
            onChange={(e) => handleLast4Change(e.target.value)}
            className="input"
            placeholder="7890"
          />
          <FormError form={form} name="last4" />
        </div>

        {data?.binInfo ? (
          <>
            <div className="md:col-span-1">
              <p className="text-sm text-gray-500">
                {translate("bank")}: <strong>{data.binInfo.bank}</strong>
              </p>
            </div>
            <div className="md:col-span-1">
              <p className="text-sm text-gray-500">
                {translate("card-type")}: <strong>{data.binInfo.brand}</strong>
              </p>
            </div>
            <div className="md:col-span-1">
              <p className="text-sm text-gray-500">
                {translate("country")}: <strong>{data.binInfo.country}</strong>
              </p>
            </div>
          </>
        ) : (
          <div className="md:col-span-2">
            <p className="text-sm text-red-500">
              {translate("card-information-not-found")}
            </p>
          </div>
        )}

        {/* Masked card */}
        <div className="md:col-span-2">
          <label className="label">{translate("masked-card")}</label>
          <input
            type="text"
            value={data.maskedCard || ""}
            className="input bg-gray-100 dark:bg-gray-700"
            disabled
          />

          <div className="flex gap-4 mt-1">
            {data.cardType && (
              <p className="text-sm text-gray-500 whitespace-nowrap">
                {translate("card-type")}: <strong>{data.cardType}</strong>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Files */}
      <FileInput
        label={translate("card-files")}
        files={data.files}
        onAdd={(newFiles) => update("files", [...data.files, ...newFiles])}
        onRemove={(idx) =>
          update(
            "files",
            data.files.filter((_, i) => i !== idx),
          )
        }
      />

      {/* Belongs to someone else */}
      <div className="items-center gap-2">
        <input
          type="checkbox"
          checked={data.isThirdParty}
          onChange={(e) => update("isThirdParty", e.target.checked)}
        />{" "}
        <label className="text-black dark:text-white ml-2">
          {translate("card-belongs-third-party")}
        </label>
      </div>

      {data.isThirdParty && (
        <div className="border rounded-lg p-4 space-y-4 bg-gray-50 dark:bg-gray-800">
          <h4 className="font-medium text-black dark:text-white">
            {translate("third-party-info")}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{translate("first-name")}</label>
              <input
                type="text"
                value={data.thirdParty.firstName}
                onChange={(e) =>
                  update("thirdParty", {
                    ...data.thirdParty,
                    firstName: e.target.value,
                  })
                }
                className="input"
              />
            </div>

            <div>
              <label className="label">{translate("last-name")}</label>
              <input
                type="text"
                value={data.thirdParty.lastName}
                onChange={(e) =>
                  update("thirdParty", {
                    ...data.thirdParty,
                    lastName: e.target.value,
                  })
                }
                className="input"
              />
            </div>
          </div>

          <FileInput
            label={translate("id-image")}
            files={data.thirdParty.idFiles}
            onAdd={(newFiles) =>
              update("thirdParty", {
                ...data.thirdParty,
                idFiles: [...data.thirdParty.idFiles, ...newFiles],
              })
            }
            onRemove={(idx) =>
              update("thirdParty", {
                ...data.thirdParty,
                idFiles: data.thirdParty.idFiles.filter((_, i) => i !== idx),
              })
            }
          />

          <ClientRelatedTo
            key={"related-to"}
            allow={companyAllow}
            relatedTo={data.relatedTo}
            onChange={handleRelatedTo}
            label={translate("related-to")}
            isMulti={true}
            readOnly={false}
            customerId={customerId}
          />

          <div className="items-center gap-2 hidden">
            <input
              type="checkbox"
              checked={data.thirdParty.authorized}
              onChange={(e) =>
                update("thirdParty", {
                  ...data.thirdParty,
                  authorized: e.target.checked,
                })
              }
            />
            <label className="text-black dark:text-white">
              {translate("authorized-card-use")}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardOwnerForm;
